import {
  loadMessages,
  selectMessages,
  selectMessagesLoading,
  selectReloadMessages,
  selectSelectedMessage,
  setMessages,
  setReloadMessages,
} from "@/features/chat/chatSlice";
import { selectAutoScrollEnabled } from "@/features/settings/settingsSlice";
import {
  selectConversations,
  selectCurrConversation,
} from "@/features/control/controlSlice";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

import { CHAT_EMPTY_CONVERSATION } from "@/constants/ui";
import { Conversation } from "@/types/conversation";
import Message from "./Message";
import { MessageType } from "@/types/message";
import { SyncLoader } from "react-spinners";
import { ChevronDown } from "lucide-react";
import clsx from "clsx";
import { isSameDay } from "@/utils/time";

// Window after an auto-scroll in which a late-decoding attachment still re-pins to the bottom.
const AUTO_SCROLL_SETTLE_MS = 1500;

const ChatMessages = () => {
  const dispatch = useAppDispatch();
  const conversations: Conversation[] = useAppSelector(selectConversations);
  const currConversationIndex = useAppSelector(selectCurrConversation);
  const reloadMessages = useAppSelector(selectReloadMessages);
  const messagesLoading = useAppSelector(selectMessagesLoading);
  const messages = useAppSelector(selectMessages);
  const selectedMessage = useAppSelector(selectSelectedMessage);
  const autoScrollEnabled = useAppSelector(selectAutoScrollEnabled);
  // When the last auto-scroll pinned the view; a ref because the mount-time media-load
  // listener can't close over live state.
  const lastAutoScrollAtRef = useRef(0);
  const prevConversationRef = useRef<Conversation | null>(null);
  const messageRefs = useRef<{
    sent: Map<number, HTMLDivElement>;
    received: Map<number, HTMLDivElement>;
  }>({
    sent: new Map(),
    received: new Map(),
  });
  const scrollRef = useRef<HTMLDivElement>(null);
  const isAtBottomRef = useRef(true);
  const reloadInFlightRef = useRef(false);
  const [hasUnread, setHasUnread] = useState(false);

  // Mini component that displays message separator if a message succeeds one on a previous day
  const MessageSeparator = ({ date }: { date: Date }) => {
    const formattedDate = date.toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });

    return (
      <div className="flex items-center justify-center gap-4 my-10 text-center">
        <p className="text-sm text-gray-400">{formattedDate}</p>
      </div>
    );
  };

  const renderMessages = useCallback(() => {
    // Only show the spinner when there's nothing to render yet. Once the list is
    // populated, keep it mounted through any background refetch — replacing it with
    // the spinner would unmount every <Message> (and its inline base64 <img>),
    // causing attachment images to flash/reload on each refresh.
    if (messagesLoading && messages.length === 0) {
      return (
        <div className="flex items-center justify-center h-full">
          <SyncLoader color="gray" />
        </div>
      );
    } else {
      return messages.map((m: MessageType, i: number) => {
        const prevMessage = messages[i - 1];
        const showMessageSeparator =
          i !== 0 &&
          !isSameDay(new Date(m.created), new Date(prevMessage.created));
        return (
          <div
            // msgIds are only unique per direction — a bare msgId key can collide
            // between a sent and a received message, breaking React reconciliation
            // (and with it the object-identity preservation that keeps unchanged
            // rows from remounting).
            key={`${m.direction}:${m.msgId}`}
            ref={(el) => {
              if (el) {
                messageRefs.current[m.direction].set(m.msgId, el);
              }
            }}
          >
            {showMessageSeparator && (
              <MessageSeparator date={new Date(m.created)} />
            )}
            <Message
              id={m.msgId}
              direction={m.direction}
              created={m.created}
              msg={m.message}
              error={m.error}
              isEncrypted={m.isEncrypted}
              isKeyExchange={m.isKeyExchange}
              senderUsername={m.senderUsername}
              isUnverified={m.isUnverified}
              isSatellite={m.isSatellite}
              lat={m.lat}
              lng={m.lng}
            />
          </div>
        );
      });
    }
  }, [messages, messagesLoading]);

  // Loads messages of selected conversation on first render or when conversation changes.
  // Messages are loaded immediately without waiting for encryption to initialize —
  // encrypted blobs show as 🔒 until own keys are ready, then ChatInputs triggers a
  // reload via setReloadMessages(true) once both own keys and the peer key are available.
  // useLayoutEffect (not useEffect) so the synchronous prefix — clearing the old
  // conversation's messages and, on a cache hit, hydrating the new one — commits before
  // the browser paints. With useEffect the old conversation could flash for a frame
  // under the newly selected one.
  useLayoutEffect(() => {
    const loadInitialMessages = async () => {
      if (currConversationIndex === null) return;

      const currConversation = conversations[currConversationIndex];
      const prevConversation = prevConversationRef.current;

      // Don't load messages if we haven't switched conversations. Compare by
      // address + addressType: addressId alone can collide between a user DM and a
      // group that share the same numeric id, which would leave the old conversation
      // in the pane.
      if (
        prevConversation &&
        prevConversation.address === currConversation.address &&
        prevConversation.addressType === currConversation.addressType
      )
        return;

      // Mark this conversation as "in-progress" before the await so that if
      // bumpConversation fires mid-load (re-sorting the list and changing
      // currConversationIndex), the re-triggered effect sees prevConversationRef
      // already pointing to currConversation and returns early.
      prevConversationRef.current = currConversation;

      // Clear the previous conversation's messages up front so the pane doesn't stay
      // stuck showing the old conversation while the new one loads. For an uncached
      // conversation this leaves messages empty → the spinner shows during the fetch.
      // For a cached one, loadMessages' synchronous hydrateFromCache refills in the same
      // render, so there's no visible empty/spinner flash.
      dispatch(setMessages([]));

      if (currConversation.updated) {
        // Plain switch → let the in-memory cache render instantly + refresh in the background.
        await dispatch(loadMessages({ ...conversations[currConversationIndex], useCache: true }));
      }
    };

    loadInitialMessages();
  }, [dispatch, conversations, currConversationIndex]);

  // Reloads messages on initial load
  useEffect(() => {
    const reloadConvMessages = async () => {
      // Guard against concurrent reloads: bumpConversation changes `conversations`
      // on every incoming message, which re-fires this effect while reloadMessages
      // is still true, spawning multiple simultaneous loadMessages calls.
      // Also guard against messagesLoading: if loadInitialMessages is in flight,
      // wait for it to finish before reloading (they'd be identical requests).
      // This effect re-fires when messagesLoading drops to false, so the reload
      // will still run after the initial load completes.
      if (!reloadMessages || currConversationIndex === null || reloadInFlightRef.current || messagesLoading) return;
      reloadInFlightRef.current = true;
      try {
        await dispatch(loadMessages(conversations[currConversationIndex]));
      } finally {
        dispatch(setReloadMessages(false));
        reloadInFlightRef.current = false;
      }
    };

    reloadConvMessages();
  }, [reloadMessages, dispatch, currConversationIndex, conversations, messagesLoading]);

  // Clear old refs and unread indicator when the open conversation changes. Keyed on identity,
  // not array index: bumpConversation re-sorts on every incoming message, shifting the index
  // without the conversation changing — which dismissed the pill and re-pinned mid-read.
  const openConversation =
    currConversationIndex !== null ? conversations[currConversationIndex] : null;
  const openConversationKey = openConversation
    ? `${openConversation.addressType}:${openConversation.address}`
    : null;
  useEffect(() => {
    messageRefs.current = { sent: new Map(), received: new Map() };
    setHasUnread(false);
    isAtBottomRef.current = true;
  }, [openConversationKey]);

  // Track whether user is scrolled to the bottom
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const handleScroll = () => {
      const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
      isAtBottomRef.current = atBottom;
      if (atBottom) setHasUnread(false);
      // Scrolling away cancels the settle window below, so a late image can't pull them back.
      else lastAutoScrollAtRef.current = 0;
    };

    el.addEventListener("scroll", handleScroll, { passive: true });
    return () => el.removeEventListener("scroll", handleScroll);
  }, []);

  // Keep pinned as attachment images finish decoding — while at the bottom, or just after an
  // auto-scroll (a message arriving with an image decodes after the pin and would otherwise
  // push itself below the fold). Outside that window a user reading history is left alone.
  // (Capture phase because <img> "load" events don't bubble.)
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const handleMediaLoad = () => {
      const justAutoScrolled = Date.now() - lastAutoScrollAtRef.current < AUTO_SCROLL_SETTLE_MS;
      if (isAtBottomRef.current || justAutoScrolled) el.scrollTop = el.scrollHeight;
    };

    el.addEventListener("load", handleMediaLoad, true);
    return () => el.removeEventListener("load", handleMediaLoad, true);
  }, []);

  // Scrolls to the selected message.
  // For new/loaded messages (scrollToBottom: true): set scrollTop directly — avoids
  // stale ref lookups and ensures isAtBottomRef stays correct after scroll.
  // For search navigation (no scrollToBottom): use scrollIntoView on the specific element.
  useEffect(() => {
    if (!selectedMessage) return;

    // Search navigation (no scrollToBottom) always jumps to the selected message — the
    // user explicitly asked for it, regardless of scroll position.
    if (!selectedMessage.scrollToBottom) {
      setTimeout(() => {
        const { id, direction } = selectedMessage;
        const messageElement = messageRefs.current[direction]?.get(id);
        if (messageElement) {
          messageElement.scrollIntoView({ behavior: "auto", block: "center" });
        }
      }, 0);
      return;
    }

    // Auto-follow to the bottom only on a forced scroll (initial load / own sent message)
    // or when the user is already at the bottom — a user scrolled up reading history is
    // never yanked, they get the unread pill instead.
    if (selectedMessage.force || (autoScrollEnabled && isAtBottomRef.current)) {
      // Defer until after the browser finishes layout on the new message list.
      // Reading scrollHeight synchronously in the effect can return a stale
      // value if the DOM hasn't been fully painted yet (common on large loads).
      requestAnimationFrame(() => {
        if (scrollRef.current) {
          scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
          // Record the pin here rather than waiting for the resulting scroll event: it fires a
          // frame later, when growing content can make the geometry read as "not at bottom"
          // and leave the ref — and the pill — stuck wrong.
          isAtBottomRef.current = true;
          lastAutoScrollAtRef.current = Date.now();
          setHasUnread(false);
        }
      });
    } else {
      // Reading history (or Auto-Scroll off) — show the unread indicator instead of yanking.
      setHasUnread(true);
    }
  }, [selectedMessage, autoScrollEnabled]);

  const scrollToLatest = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
    setHasUnread(false);
  };

  // Extra right padding: macOS/iOS overlay scrollbars draw on top of content,
  // so right-aligned rows (sent bubbles, timestamps) need clearance from the edge.
  const chatMessagesClass = clsx(
    "absolute inset-0 p-2 pr-4 md:p-4 md:pr-6 overflow-y-auto overflow-x-hidden",
    {
      "flex justify-center items-center": messages.length === 0,
    }
  );

  return (
    <div className="relative flex-grow my-2 md:my-4 min-h-0">
      <div ref={scrollRef} className={chatMessagesClass}>
        {!messagesLoading && messages.length === 0 ? (
          <p className="my-auto text-center text-neutral-400">
            {CHAT_EMPTY_CONVERSATION}
          </p>
        ) : (
          renderMessages()
        )}
      </div>
      {hasUnread && (
        <button
          onClick={scrollToLatest}
          className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5 px-3 py-1.5 bg-blue-500 text-white text-sm rounded-full shadow-md hover:bg-blue-600 transition-colors"
        >
          <ChevronDown size={14} />
          New messages
        </button>
      )}
    </div>
  );
};

export default ChatMessages;
